import React, { useEffect, useImperativeHandle, useMemo, useRef } from 'react';
import { Extension } from '@tiptap/core';
import { Fragment, Slice } from '@tiptap/pm/model';
import { EditorContent, useEditor } from '@tiptap/react';
import Placeholder from '@tiptap/extension-placeholder';
import StarterKit from '@tiptap/starter-kit';

import {
  docToText,
  normalizeNewlines,
  offsetToPos,
  posToOffset,
  textToDoc,
} from './plainTextDoc';
import { PlaceholderHighlight } from './placeholderHighlight';
import styles from './PlaceholderEditor.module.css';

/**
 * What the composer needs from the box, in the plain string's character offsets,
 * under the names a textarea uses, so the code around it reads the same.
 */
export interface PlaceholderEditorHandle {
  readonly value: string;
  readonly selectionStart: number;
  readonly selectionEnd: number;
  focus: (options?: { preventScroll?: boolean }) => void;
  setSelectionRange: (start: number, end: number) => void;
}

interface Props {
  value: string;
  onChange: (value: string) => void;
  editorRef: React.Ref<PlaceholderEditorHandle>;
  placeholder?: string;
  ariaLabel?: string;
  disabled?: boolean;
  autoFocus?: boolean;
}

// Only paragraphs and text (plus undo): the reply is plain text with line
// breaks, no marks, headings or lists (PRD 4.3).
const PLAIN_TEXT_KIT = StarterKit.configure({
  blockquote: false,
  bold: false,
  bulletList: false,
  code: false,
  codeBlock: false,
  dropcursor: false,
  gapcursor: false,
  hardBreak: false,
  heading: false,
  horizontalRule: false,
  italic: false,
  link: false,
  listItem: false,
  listKeymap: false,
  orderedList: false,
  strike: false,
  trailingNode: false,
  underline: false,
});

// Shift+Enter starts a new line like Enter does, so a line break is always a
// new paragraph and the document has one shape only.
const ShiftEnterNewLine = Extension.create({
  name: 'shiftEnterNewLine',
  addKeyboardShortcuts() {
    return { 'Shift-Enter': () => this.editor.commands.splitBlock() };
  },
});

/**
 * A plain-text box that paints [missing] and [not attached yet] in yellow.
 *
 * The highlight is a decoration inside the editor, on the very text it marks, so
 * it wraps and scrolls with that text and cannot drift from it. The value in and
 * out is a plain string; draft text is untrusted and only ever becomes text.
 *
 * Size: the box is as high as its text, up to 240 px and then scrolls, and it
 * fills any free space its container gives it. Nothing measures a height.
 */
const PlaceholderEditor: React.FC<Props> = ({
  value,
  onChange,
  editorRef,
  placeholder = '',
  ariaLabel = 'Message',
  disabled,
  autoFocus,
}) => {
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const placeholderRef = useRef(placeholder);
  placeholderRef.current = placeholder;

  const extensions = useMemo(
    () => [
      PLAIN_TEXT_KIT,
      ShiftEnterNewLine,
      Placeholder.configure({ placeholder: () => placeholderRef.current }),
      PlaceholderHighlight.configure({ className: styles.placeholder }),
    ],
    []
  );

  const editor = useEditor({
    extensions,
    content: textToDoc(value),
    editable: !disabled,
    editorProps: {
      attributes: {
        role: 'textbox',
        'aria-multiline': 'true',
        'aria-label': ariaLabel,
        class: styles.editor,
      },
      // Paste as plain text whatever the clipboard holds, keeping blank lines.
      handlePaste: (view, event) => {
        const text = event.clipboardData?.getData('text/plain');
        if (text == null) return false;
        event.preventDefault();
        const pasted = view.state.schema.nodeFromJSON(textToDoc(text));
        view.dispatch(
          view.state.tr
            .replaceSelection(new Slice(Fragment.from(pasted.content), 1, 1))
            .scrollIntoView()
        );
        return true;
      },
    },
    onUpdate: ({ editor: updated }) =>
      onChangeRef.current(docToText(updated.state.doc)),
  });

  // Opening the box puts the caret at the end of the text, as the text field did.
  // The view is focused directly: Tiptap's focus command (and its autofocus)
  // wait for a timer, and the caret has to be there when the box opens.
  useEffect(() => {
    if (!editor || !autoFocus) return;
    editor.commands.setTextSelection(editor.state.doc.content.size - 1);
    editor.view.focus();
  }, [editor, autoFocus]);

  // The text changed from outside (an AI draft, a removed placeholder, Cancel,
  // Edit on a failed send): show it without telling the composer the user typed.
  useEffect(() => {
    if (!editor) return;
    if (normalizeNewlines(value) === docToText(editor.state.doc)) return;
    editor.commands.setContent(textToDoc(value), { emitUpdate: false });
    editor.commands.setTextSelection(editor.state.doc.content.size - 1);
  }, [editor, value]);

  useEffect(() => {
    if (editor && editor.isEditable !== !disabled) {
      editor.setEditable(!disabled, false);
    }
  }, [editor, disabled]);

  useImperativeHandle(
    editorRef,
    () => ({
      get value() {
        return editor ? docToText(editor.state.doc) : '';
      },
      get selectionStart() {
        return editor
          ? posToOffset(editor.state.doc, editor.state.selection.from)
          : 0;
      },
      get selectionEnd() {
        return editor
          ? posToOffset(editor.state.doc, editor.state.selection.to)
          : 0;
      },
      focus: (options) => {
        if (!editor) return;
        editor.view.focus();
        if (!options?.preventScroll) editor.commands.scrollIntoView();
      },
      setSelectionRange: (start, end) => {
        if (!editor) return;
        const text = docToText(editor.state.doc);
        editor.commands.setTextSelection({
          from: offsetToPos(text, start),
          to: offsetToPos(text, end),
        });
      },
    }),
    [editor]
  );

  return (
    <div className={styles.wrapper}>
      <EditorContent editor={editor} className={styles.content} />
    </div>
  );
};

export default PlaceholderEditor;
